import { Award } from "@hubcapp/types";
import { InstructorBusinessProfile } from "@hubcapp/types";
import { ProfileDetailsFormFields } from "./types";

export const instructorBusinessProfileToProfileDetailsFormFields = (
  businessProfile: InstructorBusinessProfile,
): ProfileDetailsFormFields => {
  const questionAnswersMap =
    businessProfile.questionAnswers?.reduce((acc, qa) => {
      if (qa && qa.question !== null && qa.question !== undefined) {
        acc[qa.question] = qa.answer
          ? qa.answer.filter((ans): ans is string => ans !== null)
          : null;
      }
      return acc;
    }, {} as Record<string, string[] | null>) || {};

  return {
    displayName: businessProfile.displayName,
    additionalLanguages:
      businessProfile.additionalLanguages?.filter(
        (lang): lang is string => lang !== null,
      ) || null,
    badgeDate: businessProfile.badgeDate
      ? new Date(businessProfile.badgeDate)
      : null,
    teaching_enjoyment: questionAnswersMap.teaching_enjoyment || null,
    teaching_style: questionAnswersMap.teaching_style || null,
    most_rewarding_aspect: questionAnswersMap.most_rewarding_aspect || null,
    self_description: questionAnswersMap.self_description || null,
    free_time_activities: questionAnswersMap.free_time_activities || null,
    bio: businessProfile.bio,
  };
};

const awardTitles = Object.freeze({
  [Award.ClearCommunicator]: "Clear Communicator",
  [Award.ConfidenceBuilder]: "Confidence Builder",
  [Award.PerfectForBeginners]: "Perfect for Beginners",
  [Award.AboveAndBeyond]: "Above and Beyond",
  [Award.DevelopmentFocused]: "Development Focused",
  [Award.CalmUnderPressure]: "Calm Under Pressure",
  [Award.PatientAndUnderstanding]: "Patient and Understanding",
  [Award.Supportive]: "Supportive",
  [Award.FlexibleAndPunctual]: "Flexible and punctual",
  [Award.FriendlyAndPolite]: "Friendly and Polite",
});

export const getAwardTitle = (awardId: Award): string =>
  awardTitles[awardId] || "Unknown Award";

const ONE_MINUTE = 60 * 1000;
const ONE_DAY = 24 * 60 * ONE_MINUTE;
const LAST_REQUEST_TIME_KEY = "instructorBioGeneration:lastRequestTime";
const REQUEST_COUNT_KEY = "instructorBioGeneration:requestCount";

/**
 * Checks if the user has reached the limit for generating bios
 * Allows 3 request in the first 60 seconds in a 24 hour period without delays,
 * then only 1 request per 60 seconds.
 * It's frontend-only check, so it can be reset by clearing the local storage
 * @returns true if the user can generate a new bio, false otherwise
 */
export const checkBioGeneratingLimit = (): boolean => {
  const lastRequestTime =
    parseInt(localStorage.getItem(LAST_REQUEST_TIME_KEY) ?? "0") ?? 0;
  const requestCount =
    parseInt(localStorage.getItem(REQUEST_COUNT_KEY) ?? "0", 10) ?? 0;

  const now = new Date().getTime();

  //if it's the first request OR the last request was more than 24 hours ago:
  if (!lastRequestTime || !requestCount || now - lastRequestTime >= ONE_DAY) {
    setBioGenerationRequestCount(1);
    return true;
  }

  //if it's first 3 requests this day OR if the last request was more than 1 minute ago:
  if (requestCount < 3 || now - lastRequestTime >= ONE_MINUTE) {
    setBioGenerationRequestCount(requestCount + 1);
    return true;
  }

  return false;
};

const setBioGenerationRequestCount = (requestCount: number): void => {
  localStorage.setItem(LAST_REQUEST_TIME_KEY, new Date().getTime().toString());
  localStorage.setItem(REQUEST_COUNT_KEY, requestCount.toString());
};
